import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save } from 'lucide-react';
import { Section, Field, Modal } from './_shared';

const empty = { cluster: '', arabic: '', desc: '', ventures: [], order: 0 };

export default function ClustersPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { const r = await cx.get('/clusters'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (typeof payload.ventures === 'string') {
        payload.ventures = payload.ventures.split(',').map((s) => s.trim()).filter(Boolean);
      }
      if (editing.id) await cx.put(`/clusters/${editing.id}`, payload);
      else await cx.post('/clusters', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this cluster?')) return;
    try { await cx.delete(`/clusters/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Section title="NDS3 Cluster Mapping" action={<button data-testid="admin-cluster-add" onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus size={14} /> Add cluster</button>}>
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No clusters yet.</p> : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} data-testid={`admin-cluster-row-${c.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="flex-1 min-w-0">
                <p className="font-display text-prim">{c.cluster}</p>
                {c.desc && <p className="text-sec text-sm mt-1 line-clamp-2">{c.desc}</p>}
                {c.ventures?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.ventures.map((v) => (
                      <span key={v} className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-brand-teal/30 bg-brand-teal/5 text-brand-teal-light">{v}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing({ ...c, ventures: (c.ventures || []).join(', ') })} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(c.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit cluster' : 'New cluster'}>
          <div className="space-y-4">
            <Field label="Cluster name"><input className="lux-input" value={editing.cluster} onChange={(e) => setEditing({ ...editing, cluster: e.target.value })} /></Field>
            <Field label="Arabic accent"><input className="lux-input font-arabic" value={editing.arabic} onChange={(e) => setEditing({ ...editing, arabic: e.target.value })} /></Field>
            <Field label="Description"><textarea rows={3} className="lux-input resize-none" value={editing.desc} onChange={(e) => setEditing({ ...editing, desc: e.target.value })} /></Field>
            <Field label="MBK companies (comma-separated venture names)">
              <input className="lux-input" placeholder="Toothrocket, Toothmatrix, …" value={editing.ventures} onChange={(e) => setEditing({ ...editing, ventures: e.target.value })} />
              <p className="text-xs text-muted mt-2">Names should match exactly the venture names in the Ventures section.</p>
            </Field>
            <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-cluster-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
